import { useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router";
import { ArrowLeft, FileText, Upload } from "lucide-react";
import {
  Body,
  Caption,
  Card,
  CardTitle,
  EmptyState,
  ErrorMessage,
  LoadingState,
  PageHeader,
  VBadge,
  VButton,
  VTabs,
  VTabsContent,
  VTabsList,
  VTabsTrigger,
} from "@/components/ui";
import type {
  ComplianceChoice,
  ExtensionChoice,
  PolePresence,
  ReservationRequest,
} from "@/v2/shared/model";
import { formatDate, formatPeriod, fullName } from "@/v2/shared/format";
import { RequestStatusBadge } from "@/v2/shared/statusBadges";
import { findV2UserById } from "@/v2/auth/users";
import { useCurrentUser } from "@/v2/auth/useCurrentUser";
import { useRequestDetail } from "@/v2/data/queries";
import { useAddDocument } from "@/v2/data/mutations";
import { DetailActions } from "@/v2/aanvragen/detailActions";
import { CommunicationTab } from "@/v2/aanvragen/communicationTab";

const extensionLabels: Record<ExtensionChoice, string> = {
  possible: "Verlenging mogelijk",
  fixed: "Vaste einddatum",
};

const polePresenceLabels: Record<PolePresence, string> = {
  yes: "Ja",
  no: "Nee",
  unsure: "Weet ik niet zeker",
  notApplicable: "Niet van toepassing",
};

const complianceLabels: Record<ComplianceChoice, string> = {
  yes: "Ja",
  no: "Nee",
  unanswered: "Niet beantwoord",
};

// De aanvraag is bewust licht: het draait om de intake (overzicht), de
// documenten en de communicatie. Het operationele werk (inzetten, taken)
// gebeurt op het dossier, dat vanuit het overzicht bereikbaar is.
export function RequestDetailPage(): React.JSX.Element {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { data, isPending, isError } = useRequestDetail(id);
  const [tab, setTab] = useState("overview");

  if (isPending) return <LoadingState label="Aanvraag laden…" />;
  if (isError) return <ErrorMessage title="Kon de aanvraag niet laden" />;
  if (!data || data.archived) {
    return (
      <EmptyState
        title="Niet gevonden"
        description="Deze aanvraag bestaat niet (meer)."
        action={
          <VButton variant="outline" onClick={() => navigate("/aanvragen")}>
            Terug naar overzicht
          </VButton>
        }
      />
    );
  }

  const handler = findV2UserById(data.handlerId ?? "");

  return (
    <div className="space-y-6">
      <VButton
        variant="ghost"
        size="sm"
        onClick={() => navigate(-1)}
        className="-ml-3 text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden />
        Terug
      </VButton>

      <PageHeader
        title={data.title}
        subtitle={`${data.number} · ${data.address}`}
        badge={<RequestStatusBadge status={data.status} />}
        actions={
          <DetailActions
            request={data}
            onArchived={() => navigate("/aanvragen")}
            onCommentPlaced={() => setTab("activity")}
            onGoToDossier={
              data.dossiers.length === 1
                ? () => navigate(`/dossiers/${data.dossiers[0].id}`)
                : undefined
            }
          />
        }
      />

      <VTabs value={tab} onValueChange={setTab}>
        <VTabsList className="flex-wrap">
          <VTabsTrigger value="overview">Overzicht</VTabsTrigger>
          <VTabsTrigger value="documents">Documenten</VTabsTrigger>
          <VTabsTrigger value="activity">Communicatie &amp; historiek</VTabsTrigger>
        </VTabsList>

        <VTabsContent value="overview">
          <OverviewTab
            request={data}
            handlerName={handler ? fullName(handler.firstName, handler.lastName) : "-"}
          />
        </VTabsContent>
        <VTabsContent value="documents">
          <DocumentsTab request={data} />
        </VTabsContent>
        <VTabsContent value="activity">
          <CommunicationTab request={data} />
        </VTabsContent>
      </VTabs>
    </div>
  );
}

function InfoRow({ label, children }: { label: string; children: ReactNode }): React.JSX.Element {
  return (
    <div className="grid grid-cols-[9.5rem_1fr] items-baseline gap-3 py-2">
      <Caption tone="muted" className="font-medium">
        {label}
      </Caption>
      <Body>{children || "-"}</Body>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): React.JSX.Element {
  return (
    <Card padding="none" className="overflow-hidden">
      <div className="border-b border-border px-5 py-3.5">
        <CardTitle className="text-sm">{title}</CardTitle>
      </div>
      <div className="px-5 py-3">{children}</div>
    </Card>
  );
}

function OverviewTab({
  request,
  handlerName,
}: {
  request: ReservationRequest;
  handlerName: string;
}): React.JSX.Element {
  // Eén aanvraag heeft (hooguit) één dossier; de toegekende periode tonen we
  // gewoon als rij bij de periode, met een indicator wanneer ze afwijkt van
  // wat gevraagd werd.
  const dossier = request.dossiers[0];
  const dossierDiffers =
    dossier !== undefined &&
    (dossier.startDate !== request.startDate || dossier.endDate !== request.endDate);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Aanvrager">
          <InfoRow label="Naam">
            {fullName(request.requesterFirstName, request.requesterLastName)}
          </InfoRow>
          <InfoRow label="E-mail">{request.requesterEmail}</InfoRow>
          <InfoRow label="Telefoon">{request.requesterPhone}</InfoRow>
          <InfoRow label="Dienst">{request.requesterService}</InfoRow>
          <InfoRow label="Contact dienst">{request.requesterServiceContact}</InfoRow>
          <InfoRow label="Behandelaar">{handlerName}</InfoRow>
        </Section>

        <Section title="Periode">
          <InfoRow label="Begindatum">{formatDate(request.startDate)}</InfoRow>
          <InfoRow label="Beginuur">{request.startTime ?? "-"}</InfoRow>
          <InfoRow label="Einddatum">{formatDate(request.endDate)}</InfoRow>
          <InfoRow label="Verlenging">{extensionLabels[request.extension]}</InfoRow>
          {dossier ? (
            <InfoRow label="Toegekende periode">
              <span className="inline-flex flex-wrap items-center gap-2">
                <span className="tabular-nums">
                  {formatPeriod(dossier.startDate, dossier.endDate)}
                </span>
                {dossierDiffers ? <VBadge color="warning">Aangepast</VBadge> : null}
              </span>
            </InfoRow>
          ) : null}
        </Section>

        <Section title="Locatie">
          <InfoRow label="Adres">{request.address}</InfoRow>
          <InfoRow label="Stadsdeel">{request.stadsdeel ?? "-"}</InfoRow>
          <InfoRow label="Coördinaten">
            {`${request.locationLat.toFixed(5)}, ${request.locationLng.toFixed(5)}`}
          </InfoRow>
          <InfoRow label="Kijkrichting">{request.locationDescription}</InfoRow>
          <InfoRow label="Paal aanwezig">{polePresenceLabels[request.polePresence]}</InfoRow>
          <InfoRow label="Omgeving">{request.environmentalFactors}</InfoRow>
        </Section>

        <Section title="Evenement & parkeerborden">
          <InfoRow label="Evenement">{request.event}</InfoRow>
          <InfoRow label="Info evenement">{request.eventInfo}</InfoRow>
          <InfoRow label="Parkeerborden">{request.parkingSigns}</InfoRow>
        </Section>

        <Section title="Motivatie & conformiteit">
          <InfoRow label="Reden">{request.reason}</InfoRow>
          <InfoRow label="PV/dossiernr.">{request.pvNumber}</InfoRow>
          <InfoRow label="Bijz. machtiging">{request.specialAuthorization}</InfoRow>
          <InfoRow label="Meerwaarde">{request.addedValue}</InfoRow>
          <InfoRow label="Conformiteit">{complianceLabels[request.compliance]}</InfoRow>
        </Section>
      </div>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1).replace(".", ",")} MB`;
  return `${Math.max(1, Math.round(bytes / 1_000))} kB`;
}

// Documenten van de aanvraag (CB-21). Ook vanuit het dossierdetail
// raadpleegbaar: bestanden hangen centraal aan de aanvraag.
export function DocumentsTab({ request }: { request: ReservationRequest }): React.JSX.Element {
  const user = useCurrentUser();
  const author = fullName(user.firstName, user.lastName);
  const addDocument = useAddDocument();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // CB-21: bijkomende documenten toevoegen (gemockt — enkel naam en grootte
  // worden bewaard, het bestand zelf wordt niet opgeslagen).
  function handleFileChosen(files: FileList | null): void {
    const file = files?.item(0);
    if (!file) return;
    addDocument.mutate({
      requestId: request.id,
      filename: file.name,
      sizeLabel: formatFileSize(file.size),
      author,
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  const uploadButton = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        aria-hidden
        tabIndex={-1}
        onChange={(e) => handleFileChosen(e.target.files)}
      />
      <VButton
        size="sm"
        variant="outline"
        disabled={addDocument.isPending}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="size-4" aria-hidden />
        Document toevoegen
      </VButton>
    </>
  );

  if (request.documents.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">{uploadButton}</div>
        <EmptyState title="Geen documenten" description="Er zijn geen bijlagen toegevoegd." />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="flex justify-end">{uploadButton}</div>
      <Card padding="none" className="overflow-hidden">
        <ul className="divide-y divide-border">
          {request.documents.map((doc) => (
            <li key={doc.id} className="flex items-center gap-3 px-5 py-3">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <FileText className="size-4" aria-hidden />
              </span>
              <Body weight="medium" className="flex-1 truncate">
                {doc.filename}
              </Body>
              <Caption tone="muted">{doc.sizeLabel}</Caption>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
